import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Trophy, Calendar, TrendingUp, Image as ImageIcon, LogIn, GitBranch } from 'lucide-react';
import LiveMatches from '@/components/LiveMatches';
import StandingsTable from '@/components/StandingsTable';
import TopScorers from '@/components/TopScorers';
import MatchSchedule from '@/components/MatchSchedule';
import PhotoGallery from '@/components/PhotoGallery';
import KnockoutBracket from '@/components/KnockoutBracket';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AGE_GROUPS = ['Junior League', 'Champions League', 'World Cup', 'Icons League'];

export default function Home() {
  const [selectedAge, setSelectedAge] = useState('Junior League');
  const [activeTab, setActiveTab] = useState('standings');

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-card border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#0038B8] flex items-center justify-center">
                <Trophy className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white tracking-tight">Jesode United Cup 2026</h1>
                <p className="text-[#9BA3AF] text-sm">Live Scores & Updates</p>
              </div>
            </div>
            <Link to="/login">
              <Button
                data-testid="admin-login-button"
                className="bg-[#D4AF37] hover:bg-[#F3E5AB] text-[#0A0F1C] font-bold"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Admin Login
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Live Matches Ticker */}
      <LiveMatches />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Age Group Tabs */}
        <div className="mb-8">
          <h2 className="text-white text-lg font-semibold mb-4">Select League</h2>
          <div className="flex flex-wrap gap-3">
            {AGE_GROUPS.map((age) => (
              <button
                key={age}
                data-testid={`age-tab-${age}`}
                onClick={() => setSelectedAge(age)}
                className={`px-6 py-3 rounded-full font-bold transition-all ${
                  selectedAge === age
                    ? 'bg-[#D4AF37] text-[#0A0F1C] scale-105'
                    : 'bg-[#131C2F] text-white hover:bg-[#1E2A42] border border-white/10'
                }`}
              >
                {age}
              </button>
            ))}
          </div>
        </div>

        {/* Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#131C2F] border border-white/10 p-1">
            <TabsTrigger
              value="standings"
              data-testid="tab-standings"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0F1C]"
            >
              <Trophy className="w-4 h-4 mr-2" />
              Standings
            </TabsTrigger>
            <TabsTrigger
              value="matches"
              data-testid="tab-matches"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0F1C]"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Matches
            </TabsTrigger>
            <TabsTrigger
              value="bracket"
              data-testid="tab-bracket"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0F1C]"
            >
              <GitBranch className="w-4 h-4 mr-2" />
              Bracket
            </TabsTrigger>
            <TabsTrigger
              value="scorers"
              data-testid="tab-scorers"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0F1C]"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Top Scorers
            </TabsTrigger>
            <TabsTrigger
              value="gallery"
              data-testid="tab-gallery"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0F1C]"
            >
              <ImageIcon className="w-4 h-4 mr-2" />
              Gallery
            </TabsTrigger>
          </TabsList>

          <TabsContent value="standings" data-testid="standings-content">
            <StandingsTable ageGroup={selectedAge} />
          </TabsContent>

          <TabsContent value="matches" data-testid="matches-content">
            <MatchSchedule ageGroup={selectedAge} />
          </TabsContent>

          <TabsContent value="bracket" data-testid="bracket-content">
            <KnockoutBracket ageGroup={selectedAge} />
          </TabsContent>

          <TabsContent value="scorers" data-testid="scorers-content">
            <TopScorers ageGroup={selectedAge} />
          </TabsContent>

          <TabsContent value="gallery" data-testid="gallery-content">
            <PhotoGallery />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}